import { useTranslation } from 'react-i18next'

import type { LearnArticleBodyProps } from '@/features/aprender/articles'
import { DashList, Formula, LearnArticleLink, LearnSeriesNav, TipiLink } from '@/features/aprender/learnArticleShared'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article as articleUi } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const k = (field: string) => `learn.articles.food-cost-y-margen-bruto-restaurante.${field}`

const SOURCES: { labelKey: string; fallback: string }[] = [
    {
        labelKey: 'sources.1',
        fallback:
            'Ejemplo de hamburguesa (3,95 € de coste, 14,50 € PVP con IVA al 10 %): elaboración propia de Tipi en la serie de coste por plato. No es el dato de un local concreto.',
    },
    {
        labelKey: 'sources.2',
        fallback:
            'Tipo de IVA del 10 % en restauración: Agencia Tributaria, tipos impositivos en el IVA (consultar tabla vigente). El divisor 1,10 del ejemplo asume ese tipo; si tu plato lleva otro tipo, cambia el divisor.',
    },
]

export function FoodCostMargenArticle({ article }: LearnArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'error', label: t(k('toc.1'), '1. El error de mirar solo el porcentaje') },
        { id: 'sin-iva', label: t(k('toc.2'), '2. Paso 1: precio sin IVA') },
        { id: 'food-cost', label: t(k('toc.3'), '3. Paso 2: food cost') },
        { id: 'margen-bruto', label: t(k('toc.4'), '4. Paso 3: margen en euros') },
        { id: 'rompe', label: t(k('toc.5'), '5. Dónde se rompe el método') },
        { id: 'no-arregla', label: t(k('toc.6'), '6. Lo que el food cost no arregla') },
    ]

    return (
        <div className={articleUi.page}>
            <article className={articleUi.shell}>
                <ArticleCoverHero
                    category={article.category}
                    title={article.title}
                    dek={
                        <>
                            {t(
                                k('dek'),
                                'Saber que preparar una hamburguesa cuesta 3,95 € es útil. El dato empieza a valer de verdad cuando lo comparas con el precio de venta: en % y en euros.',
                            )}
                        </>
                    }
                    imageSrc={article.imagePath}
                    imageAlt={t(k('heroAlt'), 'Hamburguesa emplatada en el pase junto a una libreta')}
                    byline={
                        <ArticleByline
                            authorName={article.authorName}
                            readingMinutes={article.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />
                <ArticleReadingLayout
                    intro={
                        <div className={articleUi.proseIntro}>
                            <p className={cn(articleUi.p, articleUi.pGapMd, 'text-article-muted-soft')}>
                                {t(k('updated'), 'Actualizado el 13 de septiembre de 2026.')}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGapMd)}>
                                {t(
                                    k('intro.error'),
                                    'El día que metiste en la carta el plato «al 28 %» porque lo leíste en un grupo de WhatsApp. El coste estaba bien. El PVP, más o menos. Y aun así a final de mes no entendías por qué no sobraba nada.',
                                )}
                            </p>
                            <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                                {t(k('introBefore'), 'Si todavía no tienes el coste por ración, haz antes ')}
                                <LearnArticleLink slug='como-hacer-un-escandallo-paso-a-paso'>
                                    {t(k('linkEscandallo'), 'el escandallo paso a paso')}
                                </LearnArticleLink>
                                {t(
                                    k('introAfter'),
                                    '. Aquí partimos de la hamburguesa de Carlos: 3,95 € de coste y 14,50 € de PVP con IVA. Vamos a sacar el food cost y el margen sin convertir el 28 % en religión.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <h2 id='error' className={articleUi.h2}>
                        {t(k('h2.error'), 'El error de mirar solo el porcentaje')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.error.1'),
                                'El food cost responde a una sola pregunta: qué parte de la venta neta se va en materia prima. El margen bruto responde a otra: cuántos euros quedan después de pagar esa materia prima.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.error.2'),
                                'La trampa habitual es quedarte con el % y olvidar los euros. Un plato al 22 % que deja 4 € puede financiar menos el local que otro al 32 % que deja 11 €. Empieza siempre por quitar el IVA. Si no, el % ya nace hinchado.',
                            )}
                        </p>
                    </div>

                    <h2 id='sin-iva' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.sinIva'), 'Paso 1: saca el precio sin IVA (y no lo mezcles)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={articleUi.p}>
                            {t(
                                k('p.sinIva1'),
                                'Supongamos que vendes la hamburguesa por 14,50 €, IVA incluido al 10 %. Lo primero es eliminar el IVA:',
                            )}
                        </p>
                        <Formula variant='tight'>{t(k('formula.sinIva'), '14,50 € ÷ 1,10 = 13,18 € sin IVA')}</Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.sinIvaTrap'),
                                'Trampa del paso: usar el PVP con IVA en el denominador. Parece que el food cost es más bajo. No lo es. Estás dividiendo coste neto entre una venta que incluye dinero de Hacienda.',
                            )}
                        </p>
                        <p className={cn(articleUi.pNote, articleUi.pGap2xl)}>
                            {t(
                                k('p.sinIvaNote'),
                                'Ese 13,18 € es el ingreso neto sobre el que debes hacer los cálculos. Acción: anota PVP, tipo de IVA y neto en la misma ficha del escandallo.',
                            )}
                        </p>
                    </div>

                    <h2 id='food-cost' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.foodCost'), 'Paso 2: calcula el food cost del plato')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.foodCost1'),
                                'El food cost indica qué porcentaje de la venta neta se destina a pagar la materia prima del plato:',
                            )}
                        </p>
                        <Formula variant='tight'>
                            {t(k('formula.foodCost'), 'Food cost = Coste por ración ÷ Precio sin IVA × 100')}
                        </Formula>
                        <Formula variant='tight'>
                            {t(k('formula.foodCostExampleBefore'), '3,95 € ÷ 13,18 € × 100 = 29,97 % ≈ ')}
                            <span className={articleUi.em}>30 %</span>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.foodCost2'),
                                'Aproximadamente el 30 % de la venta neta paga ingredientes. ¿Es bueno o malo? Todavía no. Un porcentaje aislado no cuenta la historia.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.foodCostTrap'),
                                'Trampa del paso: fijar el 28 % como techo sagrado y subir precio sin mirar si el cliente lo aguanta o si el plato deja euros de verdad. Acción: escribe el % junto al margen en euros en la misma línea.',
                            )}
                        </p>
                    </div>

                    <h2 id='margen-bruto' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.margenBruto'), 'Paso 3: el margen bruto en euros')}
                    </h2>
                    <div className={articleUi.prose}>
                        <Formula variant='tight'>
                            {t(k('formula.margenBruto'), 'Margen bruto = Precio sin IVA – Coste por ración')}
                        </Formula>
                        <Formula variant='tight'>
                            {t(k('formula.margenBrutoExampleBefore'), '13,18 € − 3,95 € = ')}
                            <span className={articleUi.em}>9,23 €</span>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.margenBruto1'),
                                'La hamburguesa deja 9,23 € por unidad. Esos euros todavía no son beneficio limpio: salen sueldos, SS, alquiler, energía, seguros, comisiones y el resto.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.margenBruto2'),
                                'El escandallo no te dice todo lo que ganas. Sí te dice cuánto aporta cada plato para pagar el resto del negocio. Sin ese dato decides a oscuras.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.margenTrap'),
                                'Trampa del paso: celebrar el margen unitario y olvidar el volumen. 9,23 € × 80 ventas no es lo mismo que 9,23 € × 500. Acción: multiplica margen × unidades del último mes cerrado antes de pelearte con el precio.',
                            )}
                        </p>
                    </div>

                    <div className={articleUi.stats}>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.priceVat'), 'Precio con IVA')}</p>
                            <p className={articleUi.statValueSm}>14,50 €</p>
                        </div>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.priceNoVat'), 'Precio sin IVA')}</p>
                            <p className={articleUi.statValueSm}>13,18 €</p>
                        </div>
                        <div className={articleUi.stat}>
                            <p className={articleUi.statLabel}>{t(k('stat.costPortion'), 'Coste por ración')}</p>
                            <p className={articleUi.statValueSm}>3,95 €</p>
                        </div>
                        <div className={articleUi.statAccent}>
                            <p className={articleUi.statLabelAccent}>{t(k('stat.foodCost'), 'Food cost')}</p>
                            <p className={cn(articleUi.statValueSm, 'text-article-ember')}>≈30 %</p>
                        </div>
                        <div className={articleUi.statAccent}>
                            <p className={articleUi.statLabelAccent}>{t(k('stat.grossMargin'), 'Margen bruto')}</p>
                            <p className={cn(articleUi.statValueSm, 'text-article-ember')}>9,23 €</p>
                        </div>
                    </div>

                    <h2 id='rompe' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.rompe'), 'Dónde se rompe el método (y el caso que revienta el promedio)')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.1'),
                                'Si buscas un food cost del 28 % con 3,95 € de coste: 3,95 ÷ 0,28 = 14,11 € sin IVA → × 1,10 ≈ 15,52 € con IVA. Puedes poner 15,50 € o 15,90 €. La fórmula no es una orden divina.',
                            )}
                        </p>
                        <Formula variant='tight'>
                            {t(k('formula.noReligionBefore'), '3,95 € ÷ 0,28 = 14,11 € sin IVA → × 1,10 = ')}
                            <strong>{t(k('formula.noReligionStrong'), '15,52 € con IVA')}</strong>
                        </Formula>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.2'),
                                'Caso: dos platos. El A tiene food cost del 22 % y deja 5,10 €; vende 90 al mes → unos 459 € de margen de producto. El B tiene food cost del 31 % y deja 9,23 €; vende 500 → unos 4.615 €. El «peor» % paga más alquiler.',
                            )}
                        </p>
                        <p className={cn(articleUi.pItalic, articleUi.pGapMd)}>
                            {t(
                                k('p.rompe.note'),
                                'Ilustración propia con el margen de la hamburguesa de la serie (9,23 €) y un contraste de volumen. No es un ranking de mercado.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.rompe.3'), 'También debes valorar, además del %:')}
                        </p>
                        <DashList
                            variant='grid'
                            items={[
                                t(k('list.noReligion.1'), 'Food cost'),
                                t(k('list.noReligion.2'), 'Margen en euros'),
                                t(k('list.noReligion.3'), 'Volumen de ventas'),
                                t(k('list.noReligion.4'), 'Tiempo de elaboración'),
                                t(k('list.noReligion.5'), 'Desperdicio generado'),
                                t(k('list.noReligion.6'), 'Dificultad operativa'),
                                t(k('list.noReligion.7'), 'Posicionamiento'),
                                t(k('list.noReligion.8'), 'Precio que acepta el cliente'),
                            ]}
                        />
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('p.rompe.4'),
                                'El método se rompe cuando el coste de escandallo no es el real de cocina, cuando mezclas IVA, o cuando optimizas un plato que apenas se vende. Ahí el % es cosmética.',
                            )}
                        </p>
                    </div>

                    <h2 id='no-arregla' className={cn(articleUi.h2, articleUi.h2Spaced)}>
                        {t(k('h2.noArregla'), 'Lo que el food cost del plato no arregla')}
                    </h2>
                    <div className={articleUi.prose}>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noArregla.1'),
                                'No arregla un alquiler imposible, una plantilla sobredimensionada ni un ticket medio que no sostiene la estructura. Tampoco sustituye contrastar teórico y real en almacén.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noArregla.2Before'),
                                'Sirve para decidir precio, quitar o empujar platos y detectar si un «bestseller» te deja céntimos. Para el hueco papel vs cocina, sigue con ',
                            )}
                            <LearnArticleLink slug='coste-teorico-vs-coste-real-cocina'>
                                {t(k('linkTeoricoReal'), 'coste teórico vs coste real')}
                            </LearnArticleLink>
                            {t(k('p.noArregla.2Mid'), '. Para decidir qué queda en carta, ')}
                            <LearnArticleLink slug='disenar-carta-rentable-restaurante'>
                                {t(k('linkCarta'), 'cómo diseñar una carta más rentable')}
                            </LearnArticleLink>
                            {t(k('p.noArregla.2After'), '.')}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(k('p.noArregla.3Before'), 'Desde ')}
                            <TipiLink />
                            {t(
                                k('p.noArregla.3After'),
                                ' te ayudamos a ver el % y los euros juntos, plato a plato. No te prometemos un food cost mágico: le ponemos números a lo que ya vendes.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGapMd)}>
                            {t(
                                k('p.noArregla.4'),
                                'Si tienes el % pero no sabes si te deja euros de verdad, dínoslo y te decimos por dónde empezar.',
                            )}
                        </p>
                        <p className={cn(articleUi.p, articleUi.pGap2xl)}>
                            {t(
                                k('closing.signoff'),
                                'Si sale algo nuevo sobre esto, lo cuentas aquí antes que en ningún sitio.',
                            )}
                        </p>
                    </div>
                </ArticleReadingLayout>

                <section className={articleUi.cta}>
                    <h2 className={cn(articleUi.h2, articleUi.h2Cta)}>
                        {t(k('h2.cta'), '¿Tienes el food cost y aún no sabes cuánto te deja el plato?')}
                    </h2>
                    <p className={articleUi.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Con Tipi ves coste, % y margen en euros sin pelearte con el Excel. Dínoslo y te decimos por dónde empezar.',
                        )}
                    </p>
                    <div className={articleUi.ctaActionsCenter}>
                        <LocaleLink to='/#form' className={cn(articleUi.btn, articleUi.btnSolid)}>
                            {t(k('cta.button'), 'Empezar con Tipi')}
                        </LocaleLink>
                    </div>
                </section>

                <LearnSeriesNav currentId={article.id} />

                <section className={articleUi.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={articleUi.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={articleUi.sourcesList}>
                        {SOURCES.map(source => (
                            <li key={source.labelKey}>{t(k(source.labelKey), source.fallback)}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
